/* =========================================================================
   Settings → Legal pages (Terms of Use / Privacy Policy / Cookie Policy).

   Pure helpers (no React, no HTTP) so the rules can be unit-tested. They
   mirror the backend's src/utils/legalDocuments.js: same types, same limits,
   same publishing rules -- the backend stays the authority (422 + `errors`).
   ========================================================================= */

import { isHttpsUrl } from "./appInfoFields";

// One sub-menu item per document. `path` is where the website shows the page
// when it is written here (mode = content).
export const LEGAL_SECTIONS = [
  { key: "terms_of_use", title: "Terms of Use", path: "/terms-of-use" },
  { key: "privacy_policy", title: "Privacy Policy", path: "/privacy-policy" },
  { key: "cookie_policy", title: "Cookie Policy", path: "/cookie-policy" },
];
export const LEGAL_KEYS = LEGAL_SECTIONS.map((s) => s.key);

export const LEGAL_MODES = { CONTENT: "content", URL: "external_url" };
export const TITLE_MAX = 120;
export const URL_MAX = 2048;
export const CONTENT_MAX_BYTES = 200 * 1024;

const FORM_FIELDS = ["mode", "title", "effective_date", "content_html", "external_url", "published"];

/** Editable form values from an admin document (or defaults when missing). */
export const legalFormFrom = (doc, section) => ({
  mode: doc?.mode === LEGAL_MODES.URL ? LEGAL_MODES.URL : LEGAL_MODES.CONTENT,
  title: doc?.title ?? section?.title ?? "",
  effective_date: doc?.effective_date ?? "",
  content_html: doc?.content_html ?? "",
  external_url: doc?.external_url ?? "",
  published: Boolean(doc?.published),
});

const norm = (v) => (typeof v === "string" ? v.trim() : v);

/** True when the form differs from what was last saved. */
export const isLegalDirty = (values, saved) =>
  FORM_FIELDS.some((f) => norm(values[f]) !== norm(saved[f]));

/** True when the HTML has visible text (an empty "<p><br></p>" does not count). */
export const hasVisibleText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .trim().length > 0;

const byteLength = (s) => new Blob([String(s || "")]).size;

const isIsoDate = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

/**
 * Validate the whole form. Returns { field: message } for the invalid ones
 * (empty object = valid). Field names match the backend's `errors` keys.
 */
export const validateLegalForm = (values) => {
  const errors = {};
  const title = String(values.title || "").trim();
  const url = String(values.external_url || "").trim();
  const date = String(values.effective_date || "").trim();
  const isUrlMode = values.mode === LEGAL_MODES.URL;

  if (title.length > TITLE_MAX) errors.title = `Title must be at most ${TITLE_MAX} characters`;

  if (url) {
    if (url.length > URL_MAX) errors.external_url = `URL must be at most ${URL_MAX} characters`;
    else if (!isHttpsUrl(url)) errors.external_url = "URL must be a valid https:// link";
  }

  if (date && !isIsoDate(date)) errors.effective_date = "Effective date must be a valid date";

  if (byteLength(values.content_html) > CONTENT_MAX_BYTES) {
    errors.content_html = "Content must be at most 200 KB";
  }

  if (values.published) {
    if (!title && !errors.title) errors.title = "Title is required to publish";
    if (isUrlMode) {
      if (!url && !errors.external_url) errors.external_url = "An https:// URL is required to publish";
    } else if (!hasVisibleText(values.content_html) && !errors.content_html) {
      errors.content_html = "Write the page before publishing";
    }
  }
  return errors;
};

/** Body for PUT /config/admin/legal/:type (trimmed; empty -> null). */
export const toLegalPayload = (values) => {
  const orNull = (v) => {
    const t = String(v ?? "").trim();
    return t === "" ? null : t;
  };
  return {
    mode: values.mode,
    title: String(values.title || "").trim(),
    effective_date: orNull(values.effective_date),
    content_html: orNull(values.content_html),
    external_url: orNull(values.external_url),
    published: Boolean(values.published),
  };
};

/** First message of a backend error value (string or array). */
export const firstMessage = (v) => (Array.isArray(v) ? v[0] : v);

/**
 * Where "Preview" goes: the external URL in URL mode, otherwise the website
 * page (needs REACT_APP_WEBSITE_URL; null when it can't be built).
 */
export const legalPreviewUrl = (section, values, websiteUrl) => {
  if (values.mode === LEGAL_MODES.URL) {
    const u = String(values.external_url || "").trim();
    return isHttpsUrl(u) ? u : null;
  }
  const base = String(websiteUrl || "").trim().replace(/\/+$/, "");
  return base ? `${base}${section.path}` : null;
};

/** "Oct 23, 2026, 10:46 AM" (local), or "" for missing/invalid dates. */
export const formatUpdatedAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

// Quick-insert snippets for the HTML editor toolbar. `wrap` surrounds the
// selection (or the placeholder when nothing is selected).
export const EDITOR_TOOLS = [
  { key: "h2", label: "H2", title: "Heading", wrap: ["<h2>", "</h2>"], placeholder: "Heading" },
  { key: "h3", label: "H3", title: "Sub-heading", wrap: ["<h3>", "</h3>"], placeholder: "Sub-heading" },
  { key: "p", label: "¶", title: "Paragraph", wrap: ["<p>", "</p>"], placeholder: "Paragraph" },
  { key: "b", label: "B", title: "Bold", wrap: ["<strong>", "</strong>"], placeholder: "bold text" },
  { key: "i", label: "I", title: "Italic", wrap: ["<em>", "</em>"], placeholder: "italic text" },
  { key: "ul", label: "• List", title: "Bullet list", wrap: ["<ul>\n  <li>", "</li>\n</ul>"], placeholder: "Item" },
  { key: "ol", label: "1. List", title: "Numbered list", wrap: ["<ol>\n  <li>", "</li>\n</ol>"], placeholder: "Item" },
  { key: "a", label: "Link", title: "Link", wrap: ['<a href="https://">', "</a>"], placeholder: "link text" },
  {
    key: "table",
    label: "Table",
    title: "Table",
    wrap: ["<table>\n  <thead><tr><th>Heading</th><th>Heading</th></tr></thead>\n  <tbody><tr><td>", "</td><td>Cell</td></tr></tbody>\n</table>"],
    placeholder: "Cell",
  },
];

/** Apply a toolbar snippet to text with a selection. Returns { text, start, end }. */
export const applyEditorTool = (text, selStart, selEnd, tool) => {
  const before = text.slice(0, selStart);
  const selected = text.slice(selStart, selEnd) || tool.placeholder;
  const after = text.slice(selEnd);
  const [open, close] = tool.wrap;
  const start = before.length + open.length;
  return { text: `${before}${open}${selected}${close}${after}`, start, end: start + selected.length };
};
